/**
 * Reusable custom select dropdown utility
 * @param {string} selector - CSS selector for the select(s)
 * @param {Object} [config] - Optional config for class names and callbacks
 */
const initializeCustomSelectFor = (selector, config = {}) => {
  const defaultConfig = {
    wrapperClass: 'select-wrapper',
    dropdownClass: 'custom-select-dropdown',
    itemClass: 'custom-select-item',
    activeClass: 'active',
    showClass: 'show',
    selectedClass: 'selected',
    onChange: null,
  };
  const cfg = { ...defaultConfig, ...config };
  const selects = document.querySelectorAll(selector);
  selects.forEach(select => {
    const wrapper = select.closest('.' + cfg.wrapperClass);
    if (!wrapper) return;

    // Prevent duplicate dropdowns
    if (wrapper.querySelector('.' + cfg.dropdownClass)) return;

    // Create custom dropdown
    const customDropdown = document.createElement('div');
    customDropdown.className = cfg.dropdownClass;

    // Add options to custom dropdown
    Array.from(select.options).forEach(option => {
      const item = document.createElement('div');
      item.className = cfg.itemClass;
      item.textContent = option.textContent;
      if (option.selected) item.classList.add(cfg.selectedClass);
      item.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('change'));
        customDropdown.querySelectorAll('.' + cfg.itemClass).forEach(i => i.classList.remove(cfg.selectedClass));
        item.classList.add(cfg.selectedClass);
        if (typeof cfg.onChange === 'function') cfg.onChange(option.value, select);
        customDropdown.classList.remove(cfg.showClass);
        wrapper.classList.remove(cfg.activeClass);
      });
      customDropdown.appendChild(item);
    });

    wrapper.appendChild(customDropdown);

    // Show/hide custom dropdown on select click
    select.addEventListener('mousedown', e => {
      e.preventDefault();
      const isVisible = customDropdown.classList.contains(cfg.showClass);
      // Close all other dropdowns
      document.querySelectorAll('.' + cfg.dropdownClass).forEach(dropdown => {
        dropdown.classList.remove(cfg.showClass);
        dropdown.closest('.' + cfg.wrapperClass).classList.remove(cfg.activeClass);
      });
      // Toggle current dropdown
      if (!isVisible) {
        customDropdown.classList.add(cfg.showClass);
        wrapper.classList.add(cfg.activeClass);
      }
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', e => {
      if (!wrapper.contains(e.target)) {
        customDropdown.classList.remove(cfg.showClass);
        wrapper.classList.remove(cfg.activeClass);
      }
    });
  });
};
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

const ERROR_MESSAGES = {
  ELEMENT_NOT_FOUND: 'Mega Menu: Element not found',
  INIT_FAILED: 'Mega Menu: Initialization failed',
  SWIPER_INIT_FAILED: 'Mega Menu: Swiper initialization failed',
};

const CONFIG = {
  HOVER_DELAY: 150,
  SLIDE_SPEED: 800,
  SLIDE_SPACING: 24,
  BREAKPOINTS: {
    MOBILE: 320,
    TABLET: 768,
    DESKTOP: 1024,
  },
  SLIDES_PER_VIEW: {
    MOBILE: 1,
    TABLET: 2,
    DESKTOP: 3,
  },
  CLASSES: {
    SHOW: 'show',
    ACTIVE: 'active',
    DISABLED: 'swiper-button-disabled',
  },
  SELECTORS: {
    MEGA_MENU: '.mega-menu',
    CONTAINER: '.mega-menu__container',
    TAB_LINKS: '.mega-menu__nav-item',
    TAB_CONTENTS: '.mega-menu__tab-content',
    NAV_BUTTONS: '.mega-menu__nav-btn',
    PREV_BUTTON: '.mega-menu__nav-btn--prev',
    NEXT_BUTTON: '.mega-menu__nav-btn--next',
    DROPDOWN_TOGGLE: '.dropdown-toggle',
  },
};

/**
 * Initialize all mega menu functionality
 * Finds all mega menus on the page and initializes them independently
 * @returns {void}
 */
const initializeMegaMenu = () => {
  try {
    // Find all mega menus on the page
    const megaMenus = document.querySelectorAll(CONFIG.SELECTORS.MEGA_MENU);

    if (megaMenus.length === 0) {
      console.warn(ERROR_MESSAGES.ELEMENT_NOT_FOUND);
      return;
    }

    // Initialize each mega menu independently
    megaMenus.forEach(megaMenu => {
      initializeSingleMegaMenu(megaMenu);
    });

    // Add global click outside handler
    initializeClickOutsideHandler();
  } catch (error) {
    console.error(ERROR_MESSAGES.INIT_FAILED, error);
  }
};

/**
 * Initialize click outside handler to close mega menus
 * @returns {void}
 */
const initializeClickOutsideHandler = () => {
  document.addEventListener('click', e => {
    // Check if click is outside any mega menu
    const clickedMegaMenu = e.target.closest(CONFIG.SELECTORS.MEGA_MENU);
    const clickedDropdownToggle = e.target.closest('.dropdown-toggle');

    if (!clickedMegaMenu && !clickedDropdownToggle) {
      // Click is outside, close all mega menus
      closeAllMegaMenus();
    }
  });
};

/**
 * Initialize a single mega menu instance
 * @param {HTMLElement} megaMenu - The mega menu element
 * @returns {void}
 */
const initializeSingleMegaMenu = megaMenu => {
  // Initialize custom select for .mega-menu__state-select inside this mega menu
  initializeCustomSelectFor('.mega-menu__state-select');
  try {
    // Find associated elements for this specific mega menu
    const megaMenuContainer = megaMenu.querySelector(CONFIG.SELECTORS.CONTAINER);
    const megaMenuId = megaMenu.id;

    if (!megaMenuContainer || !megaMenuId) {
      console.warn(`${ERROR_MESSAGES.ELEMENT_NOT_FOUND}: Container or ID missing for mega menu`);
      return;
    }

    // Find the toggle button and dropdown item using the mega menu ID
    const toggleButton =
      document.querySelector(`[aria-controls="${megaMenuId}"], [data-bs-target="#${megaMenuId}"]`) ||
      document.querySelector(`#${megaMenuId.replace('Dropdown', 'Toggle')}`);

    const dropdownItem = toggleButton?.closest('.dropdown.nav-item');

    if (!toggleButton || !dropdownItem) {
      console.warn(`${ERROR_MESSAGES.ELEMENT_NOT_FOUND}: Toggle button or dropdown item for ${megaMenuId}`);
      return;
    }

    megaMenu.querySelectorAll('.mega-menu__card').forEach(card => {
      card.addEventListener('click', e => {
        var link = card.querySelector('a');
        if (link && !e.target.closest('a')) link.click();
      });
    });

    megaMenu.querySelectorAll('.mega-menu__view-issues-btn, .mega-menu__sub-issue-tag').forEach(btn => {
      btn.addEventListener('click', e => {
        sessionStorage.setItem('scrollToFilters', '1');
      });
    });

    // Initialize components for this mega menu
    initializeHoverInteractions(megaMenu, dropdownItem, toggleButton);
    initializeTabSwitching(megaMenu);
    initializeNavigationButtons(megaMenu);
    initializeActiveTabSwiper(megaMenu);
    initializeButtonUrlOnSelectChange(megaMenu);
    initializeButtonUrlOnSelectChange(megaMenu);

    document.querySelectorAll('.mobile-state-menu').forEach(el => {
      initializeButtonUrlOnSelectChange(el);
    });
  } catch (error) {
    console.error(`${ERROR_MESSAGES.INIT_FAILED} for mega menu`, error);
  }
};

/**
 * Initialize hover interactions for smooth animations
 * @param {HTMLElement} megaMenu - The mega menu element
 * @param {HTMLElement} dropdownItem - The dropdown item element
 * @param {HTMLElement} toggleButton - The toggle button element
 * @returns {void}
 */
const initializeHoverInteractions = (megaMenu, dropdownItem, toggleButton) => {
  let hoverTimeout;

  /**
   * Show the mega menu
   * @returns {void}
   */
  const showMegaMenu = () => {
    clearTimeout(hoverTimeout);
    megaMenu.classList.add(CONFIG.CLASSES.SHOW);

    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'true');
    }
  };

  /**
   * Hide the mega menu with delay
   * @returns {void}
   */
  const hideMegaMenu = () => {
    hoverTimeout = setTimeout(() => {
      megaMenu.classList.remove(CONFIG.CLASSES.SHOW);

      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', 'false');
      }
    }, CONFIG.HOVER_DELAY);
  };

  /**
   * Hide the mega menu immediately
   * @returns {void}
   */
  const hideMegaMenuImmediate = () => {
    megaMenu.classList.remove(CONFIG.CLASSES.SHOW);

    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'false');
    }
  };

  // Add event listeners
  dropdownItem.addEventListener('mouseenter', showMegaMenu);
  dropdownItem.addEventListener('mouseleave', hideMegaMenu);
  megaMenu.addEventListener('mouseenter', () => clearTimeout(hoverTimeout));
  megaMenu.addEventListener('mouseleave', hideMegaMenuImmediate);

  // Prevent default link behavior on toggle button
  if (toggleButton) {
    toggleButton.addEventListener('click', e => {
      e.preventDefault();
    });
  }

  // Initialize navigation link handling
  initializeNavigationLinks(megaMenu, hideMegaMenuImmediate);
};

/**
 * Initialize navigation links to close mega menu when clicked
 * @param {HTMLElement} megaMenu - The mega menu element
 * @param {Function} hideMegaMenu - Function to hide the mega menu
 * @returns {void}
 */
const initializeNavigationLinks = (megaMenu, hideMegaMenu) => {
  // Find all navigation links within the mega menu
  const navigationLinks = megaMenu.querySelectorAll('a[href]');

  navigationLinks.forEach(link => {
    // Skip tab navigation links (they have data-tab attribute or are within nav-item)
    if (link.closest('.mega-menu__nav-item')) {
      return;
    }

    // Skip links that are just anchors or javascript
    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('javascript:')) {
      return;
    }

    // Skip share buttons and other action buttons
    if (link.classList.contains('widget-latest-news__card-share') || link.classList.contains('mega-menu__card-share')) {
      return;
    }

    // Add click event to close mega menu for actual navigation links
    link.addEventListener('click', e => {
      const isExternalLink =
        link.getAttribute('target') === '_blank' || (href && href.startsWith('http') && !href.startsWith(window.location.origin));

      if (isExternalLink) {
        // Force external link to open using window.open as fallback
        const target = link.getAttribute('target') || '_blank';
        const rel = link.getAttribute('rel') || 'noopener noreferrer';

        // Try to open the link programmatically
        try {
          const newWindow = window.open(href, target, 'noopener,noreferrer');
          if (newWindow) {
            newWindow.opener = null; // Security measure
          }
        } catch (error) {
          console.warn('Failed to open external link programmatically:', error);
        }

        // Close mega menu after a short delay
        setTimeout(() => hideMegaMenu(), 100);
      } else {
        // For internal links, close immediately
        hideMegaMenu();
      }
    });
  });

  // Also handle buttons that might navigate
  const navigationButtons = megaMenu.querySelectorAll('button[type="submit"], .btn[href]');
  navigationButtons.forEach(button => {
    // Skip navigation buttons within the mega menu
    if (button.closest('.mega-menu__content-nav') || button.classList.contains('mega-menu__nav-btn')) {
      return;
    }

    // Add click event to close mega menu for navigation buttons
    button.addEventListener('click', e => {
      // Don't prevent default - let the button work normally
      // Just close the mega menu
      hideMegaMenu();
    });
  });
};

/**
 * Initialize tab switching functionality for a specific mega menu
 * @param {HTMLElement} megaMenu - The mega menu element
 * @returns {void}
 */
const initializeTabSwitching = megaMenu => {
  const tabLinks = megaMenu.querySelectorAll(CONFIG.SELECTORS.TAB_LINKS);
  const tabContents = megaMenu.querySelectorAll(CONFIG.SELECTORS.TAB_CONTENTS);

  /**
   * Handle tab click events
   * @param {Event} e - Click event
   * @returns {void}
   */
  const handleTabClick = e => {
    e.preventDefault();
    e.stopPropagation();

    const tabType = e.currentTarget.getAttribute('data-tab');

    // Remove active class from all tabs in this mega menu
    tabLinks.forEach(link => link.classList.remove(CONFIG.CLASSES.ACTIVE));

    // Add active class to clicked tab
    e.currentTarget.classList.add(CONFIG.CLASSES.ACTIVE);

    // Hide all tab contents in this mega menu
    tabContents.forEach(content => {
      content.style.display = 'none';
      content.classList.remove(CONFIG.CLASSES.ACTIVE);
    });

    // Show selected tab content
    const selectedTabContent = megaMenu.querySelector(`.mega-menu__tab-content[data-tab="${tabType}"]`);
    if (selectedTabContent) {
      selectedTabContent.style.display = 'block';
      selectedTabContent.classList.add(CONFIG.CLASSES.ACTIVE);

      // Initialize swiper for this tab
      initializeTabSwiper(megaMenu, tabType);
    }
  };

  // Add event listeners to tab links
  tabLinks.forEach(tabLink => {
    tabLink.addEventListener('click', handleTabClick);
  });
};

/**
 * Initialize navigation buttons for a specific mega menu
 * @param {HTMLElement} megaMenu - The mega menu element
 * @returns {void}
 */
const initializeNavigationButtons = megaMenu => {
  const navButtons = megaMenu.querySelectorAll(CONFIG.SELECTORS.NAV_BUTTONS);

  navButtons.forEach(button => {
    button.addEventListener('click', e => {
      e.stopPropagation();
    });
  });
};

/**
 * Initialize swiper for the active tab on load for a specific mega menu
 * @param {HTMLElement} megaMenu - The mega menu element
 * @returns {void}
 */
const initializeActiveTabSwiper = megaMenu => {
  const activeTab = megaMenu.querySelector('.mega-menu__nav-item.active');
  if (activeTab) {
    const activeTabType = activeTab.getAttribute('data-tab');
    initializeTabSwiper(megaMenu, activeTabType);
  }
};

/**
 * Initialize swiper for a specific tab within a specific mega menu
 * @param {HTMLElement} megaMenu - The mega menu element
 * @param {string} tabType - The tab type identifier
 * @returns {void}
 */
const initializeTabSwiper = (megaMenu, tabType) => {
  const swiperContainer = megaMenu.querySelector(`.mega-menu__slider[data-swiper="${tabType}"]`);
  if (!swiperContainer) {
    console.warn(`${ERROR_MESSAGES.ELEMENT_NOT_FOUND}: Swiper container for tab "${tabType}" in mega menu ${megaMenu.id}`);
    return;
  }

  try {
    // Destroy existing swiper if it exists
    if (swiperContainer.swiper) {
      swiperContainer.swiper.destroy(true, true);
    }

    // Get navigation buttons for this specific tab within this mega menu
    const tabContent = megaMenu.querySelector(`.mega-menu__tab-content[data-tab="${tabType}"]`);
    const prevButton = tabContent?.querySelector(CONFIG.SELECTORS.PREV_BUTTON);
    const nextButton = tabContent?.querySelector(CONFIG.SELECTORS.NEXT_BUTTON);

    // Initialize new swiper
    const swiper = new Swiper(swiperContainer, {
      modules: [Navigation],
      slidesPerView: CONFIG.SLIDES_PER_VIEW.DESKTOP,
      spaceBetween: CONFIG.SLIDE_SPACING,
      speed: CONFIG.SLIDE_SPEED,
      effect: 'slide',
      navigation: {
        nextEl: nextButton,
        prevEl: prevButton,
        disabledClass: CONFIG.CLASSES.DISABLED,
      },
      watchOverflow: true,
      grabCursor: true,
      preventInteractionOnTransition: true,
      breakpoints: {
        [CONFIG.BREAKPOINTS.MOBILE]: {
          slidesPerView: CONFIG.SLIDES_PER_VIEW.MOBILE,
          spaceBetween: CONFIG.SLIDE_SPACING,
        },
        [CONFIG.BREAKPOINTS.TABLET]: {
          slidesPerView: CONFIG.SLIDES_PER_VIEW.TABLET,
          spaceBetween: CONFIG.SLIDE_SPACING,
        },
        [CONFIG.BREAKPOINTS.DESKTOP]: {
          slidesPerView: CONFIG.SLIDES_PER_VIEW.DESKTOP,
          spaceBetween: CONFIG.SLIDE_SPACING,
        },
      },
      on: {
        init: function () {
          updateNavigationButtons(this, prevButton, nextButton);
        },
        slideChange: function () {
          updateNavigationButtons(this, prevButton, nextButton);
        },
      },
    });
  } catch (error) {
    console.error(`${ERROR_MESSAGES.SWIPER_INIT_FAILED} for tab "${tabType}" in mega menu ${megaMenu.id}`, error);
  }
};

/**
 * Update navigation button states based on swiper position
 * @param {Object} swiper - Swiper instance
 * @param {HTMLElement} prevButton - Previous button element
 * @param {HTMLElement} nextButton - Next button element
 * @returns {void}
 */
const updateNavigationButtons = (swiper, prevButton, nextButton) => {
  if (!prevButton || !nextButton) return;

  // Update previous button state
  prevButton.disabled = swiper.isBeginning;

  // Update next button state
  nextButton.disabled = swiper.isEnd;
};

/**
 * Initialize button url on sSelect Change
 * @param {HTMLElement} megaMenu - The mega menu element
 * @returns {void}
 */
const initializeButtonUrlOnSelectChange = megaMenu => {
  const select = megaMenu.querySelector('.mega-menu__state-select');
  const selectWrapper = megaMenu.querySelector('.mega-menu__location-form');
  const btn = megaMenu.querySelector('.mega-menu__get-started-btn');

  if (select && selectWrapper && btn) {
    select.addEventListener('change', () => {
      const url = select.value;
      if (url) {
        btn.setAttribute('href', url);
      } else {
        btn.setAttribute('href', '#');
      }
    });

    // Also close mega menu when button is clicked (if it has a valid href)
    btn.addEventListener('click', e => {
      const href = btn.getAttribute('href');
      if (href && href !== '#') {
        // Find the mega menu and close it
        const megaMenuElement = btn.closest('.mega-menu');
        if (megaMenuElement) {
          megaMenuElement.classList.remove(CONFIG.CLASSES.SHOW);
          // Also update the toggle button state
          const megaMenuId = megaMenuElement.id;
          const toggleButton =
            document.querySelector(`[aria-controls="${megaMenuId}"], [data-bs-target="#${megaMenuId}"]`) ||
            document.querySelector(`#${megaMenuId.replace('Dropdown', 'Toggle')}`);
          if (toggleButton) {
            toggleButton.setAttribute('aria-expanded', 'false');
          }
        }
      }
    });
  }
};

/**
 * Close all mega menus on the page
 * @returns {void}
 */
const closeAllMegaMenus = () => {
  const megaMenus = document.querySelectorAll(CONFIG.SELECTORS.MEGA_MENU);
  megaMenus.forEach(megaMenu => {
    megaMenu.classList.remove(CONFIG.CLASSES.SHOW);

    // Update toggle button states
    const megaMenuId = megaMenu.id;
    const toggleButton =
      document.querySelector(`[aria-controls="${megaMenuId}"], [data-bs-target="#${megaMenuId}"]`) ||
      document.querySelector(`#${megaMenuId.replace('Dropdown', 'Toggle')}`);
    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'false');
    }
  });
};

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', initializeMegaMenu);

// Export for use in other modules
export { initializeMegaMenu, closeAllMegaMenus };
